import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { List as ImmutableList, List } from 'immutable';
import { debounce } from 'lodash-decorators';
import * as constants from '../../constants-types/constants';
import { FilterInfo, FilterType, User } from '../../core-data-model';
import { UserDataService } from '../../core-services';

@Component({
    selector: 'share-group-with-user-pillbox',
    template: `
        <ok-pillbox-with-typeahead
            [(ngModel)]="boundValue"
            (typeaheadOnTextChange)="typeaheadOnTextChange($event)"
            [typeaheadList]="typeaheadList"
            [typeaheadPlaceholder]="placeholder"
            [searchStyling]="true"
            [isLoadingPossibilities]="loadingUsers"
            (itemSelected)="userAdded.emit($event)"
            (onRemove)="userRemoved.emit($event)"
        >
            <ng-template #pillbox let-item="item">
                {{ item.getFullDisplayName() }}
            </ng-template>
            <ng-template #typeahead let-item="item">
                {{ item.getFullDisplayName() }}
            </ng-template>
        </ok-pillbox-with-typeahead>
    `,
    // Following is required for ControlValueAccessor
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: ShareGroupWithUserPillboxComponent,
            multi: true
        }
    ],
    standalone: false
})
export class ShareGroupWithUserPillboxComponent
    implements ControlValueAccessor
{
    sharedUsers: List<User>;

    @Input()
    placeholder: string;

    @Input()
    areThirdPartyUsers = false;

    @Output()
    userAdded: EventEmitter<User> = new EventEmitter<User>();

    @Output()
    userRemoved: EventEmitter<User> = new EventEmitter<User>();

    typeaheadList: List<User> = ImmutableList();
    loadingUsers = false;

    constructor(private user: UserDataService) {}

    typeaheadOnTextChange(textInBox: string) {
        if (!textInBox) {
            return;
        }
        this.loadingUsers = true;
        this.searchUsers(textInBox);
    }

    @debounce(constants.peopleSearchDelay)
    private searchUsers(searchText: string) {
        const filters: FilterInfo[] = [
            new FilterInfo({
                type: FilterType.isAccessEnabled
            }),
            new FilterInfo({
                type: FilterType.Search,
                parameters: { term: searchText }
            })
        ];
        this.user
            .getUsers(
                5,
                filters,
                this.areThirdPartyUsers ? 'thirdParty' : 'users'
            )
            .then((users) => {
                this.typeaheadList = ImmutableList(
                    users.filter(
                        (retrievedUser: User) =>
                            this.sharedUsers.findIndex(
                                (currentUser: User) =>
                                    currentUser.id === retrievedUser.id
                            ) === -1
                    )
                );
            })
            .finally(() => {
                this.loadingUsers = false;
            });
    }

    /** ***************************************/
    // Value needed by ControlValueAccessor - it is a no-op that is replaced by framework
    private propagateChange: Function = () => {
        /* No-op required by ControlValueAccessor interface - see registerOnChange */
    };

    get boundValue(): List<User> {
        return this.sharedUsers;
    }
    set boundValue(val: List<User>) {
        this.sharedUsers = val;
        this.propagateChange(this.sharedUsers);
    }

    /* Meet ControlValueAccessor Interface */
    // Do NOT propagate changes up here
    writeValue(value: List<User>): void {
        this.sharedUsers = value;
    }
    registerOnChange(fn: any): void {
        this.propagateChange = (changed: any) => fn.call(this, changed);
    }
    registerOnTouched(): void {
        /* No callback function registered OnTouched */
    }
    /** ***************************************/
}
